/**
 * Policy change rules, shared by the store (UI guard) and the mock server (the server-side
 * check a real backend must also make). A change either tightens a limit (one tap) or
 * loosens it (1.5 s hold plus a written reason). Structural rules come first: bounds, the
 * warm-up ceiling, the team cap, locked stages and unmet prerequisites refuse a change in
 * any case.
 */

import { formatNumber, formatPctValue, formatUsd } from '../lib/format.js';
import type {
  ChangeDirection,
  FeatureFlag,
  NumberPolicy,
  PolicyChange,
  PolicyChangeInput,
  Settings,
  StagePolicy,
} from './settings.js';

/** A reason shorter than this (after trimming) does not count as a reason. */
export const MIN_REASON_LENGTH = 5;

export type ViolationCode =
  | 'unknown'
  | 'range'
  | 'ceiling'
  | 'team_cap'
  | 'below_spend'
  | 'stage_locked'
  | 'prerequisites'
  | 'reason_required'
  | 'hold_required';

export interface PolicyViolation {
  code: ViolationCode;
  message: string;
}

export interface ChangeEvaluation {
  direction: ChangeDirection;
  label: string;
  from: string;
  to: string;
  violation: PolicyViolation | null;
}

/** "30 $", "0,50 $", "10 sn", "1,5 sn", "48 sa", "30 gün", "2 yıl", "%20", "0 / gün". */
export function formatPolicyValue(policy: Pick<NumberPolicy, 'unit'>, value: number): string {
  switch (policy.unit) {
    case 'usd':
      return formatUsd(value, 'auto');
    case 'ms':
      return `${formatNumber(value / 1000, value % 1000 === 0 ? 0 : 1)} sn`;
    case 'hours':
      return `${formatNumber(value)} sa`;
    case 'days':
      return value >= 365 && value % 365 === 0 ? `${value / 365} yıl` : `${formatNumber(value)} gün`;
    case 'percent':
      return formatPctValue(value);
    case 'count':
      return `${formatNumber(value)} / gün`;
  }
}

export function flagState(enabled: boolean): string {
  return enabled ? 'Açık' : 'Kapalı';
}

function numberDirection(policy: NumberPolicy, value: number): ChangeDirection {
  if (value === policy.value) return 'none';
  const raising = value > policy.value;
  return raising === (policy.loosens === 'up') ? 'loosen' : 'tighten';
}

/**
 * Sum of the real spend budgets (Genesis, media, infrastructure, sales engine) with one value
 * replaced. It mirrors the config loader: agents billed for real plus every budget line of
 * config/policies.yaml.
 */
export function realSpendTotal(settings: Settings, replace: { id: string; value: number } | null = null): number {
  return settings.policies
    .filter((policy) => policy.countsTowardCap)
    .reduce((sum, policy) => sum + (replace && policy.id === replace.id ? replace.value : policy.value), 0);
}

function numberViolation(settings: Settings, policy: NumberPolicy, value: number): PolicyViolation | null {
  if (!Number.isFinite(value) || value < policy.min || value > policy.max) {
    return {
      code: 'range',
      message: `${policy.label} ${formatPolicyValue(policy, policy.min)} ile ${formatPolicyValue(policy, policy.max)} arasında olmalı`,
    };
  }
  const direction = numberDirection(policy, value);
  if (direction === 'loosen' && policy.ceiling && value > policy.ceiling.value) {
    return { code: 'ceiling', message: `${policy.label} şu an artırılamaz: ${policy.ceiling.reason}` };
  }
  const cap = settings.policies.find((item) => item.id === 'budget_team_cap');
  if (policy.countsTowardCap && direction === 'loosen' && cap) {
    const total = realSpendTotal(settings, { id: policy.id, value });
    if (total > cap.value) {
      return {
        code: 'team_cap',
        message: `Ekip tavanını aşar: ${formatUsd(total, 'auto')} / ${formatUsd(cap.value, 'auto')}. Önce tavanı artır`,
      };
    }
  }
  if (policy.id === 'budget_team_cap' && direction === 'tighten') {
    const total = realSpendTotal(settings);
    if (value < total) {
      return {
        code: 'below_spend',
        message: `Bütçelerin toplamı ${formatUsd(total, 'auto')}; tavan bunun altına inemez. Önce bir bütçeyi düşür`,
      };
    }
  }
  return null;
}

function stageViolation(stage: StagePolicy, value: number): PolicyViolation | null {
  const option = stage.stages.find((item) => item.value === value);
  if (!option) return { code: 'range', message: `${stage.label} için böyle bir aşama yok` };
  if (!option.unlocked && value > stage.value) {
    return { code: 'stage_locked', message: `Aşama ${value} kilitli: ${option.condition}` };
  }
  return null;
}

function flagViolation(flag: FeatureFlag, enabled: boolean): PolicyViolation | null {
  if (enabled === flag.safeValue) return null;
  const unmet = flag.prerequisites.filter((item) => !item.met);
  if (unmet.length === 0) return null;
  const list = unmet.map((item) => (item.progress ? `${item.label} (${item.progress})` : item.label)).join(', ');
  return { code: 'prerequisites', message: `${flag.label} açılamaz. Önce: ${list}` };
}

/**
 * What a change that arrived over the wire must look like before any rule reads it. A real
 * backend parses the request with a schema; the mock and the store check the same shape so
 * that `{ kind: 'hard' }`, a string value or `enabled: 0` is refused, not half-applied.
 */
function malformed(change: unknown): PolicyViolation | null {
  if (typeof change !== 'object' || change === null) return { code: 'unknown', message: 'Değişiklik yok' };
  const { kind, id } = change as { kind?: unknown; id?: unknown };
  if (typeof id !== 'string') return { code: 'unknown', message: 'Ayar kimliği yok' };
  switch (kind) {
    case 'number':
    case 'stage':
      return Number.isFinite((change as { value?: unknown }).value)
        ? null
        : { code: 'range', message: `${id}: değer bir sayı olmalı` };
    case 'flag':
    case 'agent':
      return typeof (change as { enabled?: unknown }).enabled === 'boolean'
        ? null
        : { code: 'range', message: `${id}: açık ya da kapalı olmalı` };
    default:
      return { code: 'unknown', message: `Bilinmeyen ayar: ${id}; bu tür ayar panelden değişmez` };
  }
}

/** Direction, before and after values, and any structural rule the change breaks. */
export function evaluateChange(settings: Settings, change: PolicyChange): ChangeEvaluation {
  const unknown = (label: string): ChangeEvaluation => ({
    direction: 'none',
    label,
    from: '',
    to: '',
    violation: { code: 'unknown', message: `Bilinmeyen ayar: ${label}` },
  });
  const shape = malformed(change);
  if (shape !== null) return { direction: 'none', label: '', from: '', to: '', violation: shape };
  switch (change.kind) {
    case 'number': {
      const policy = settings.policies.find((item) => item.id === change.id);
      if (!policy) return unknown(change.id);
      return {
        direction: numberDirection(policy, change.value),
        label: policy.label,
        from: formatPolicyValue(policy, policy.value),
        to: formatPolicyValue(policy, change.value),
        violation: numberViolation(settings, policy, change.value),
      };
    }
    case 'stage': {
      const stage = settings.stage;
      const stageId: unknown = change.id;
      if (stageId !== stage.id) return unknown(String(stageId));
      const direction: ChangeDirection =
        change.value === stage.value ? 'none' : change.value > stage.value ? 'loosen' : 'tighten';
      return {
        direction,
        label: stage.label,
        from: `Aşama ${stage.value}`,
        to: `Aşama ${change.value}`,
        violation: stageViolation(stage, change.value),
      };
    }
    case 'flag': {
      const flag = settings.flags.find((item) => item.id === change.id);
      if (!flag) return unknown(change.id);
      const direction: ChangeDirection =
        change.enabled === flag.enabled ? 'none' : change.enabled === flag.safeValue ? 'tighten' : 'loosen';
      return {
        direction,
        label: flag.label,
        from: flagState(flag.enabled),
        to: flagState(change.enabled),
        violation: flagViolation(flag, change.enabled),
      };
    }
    case 'agent': {
      const agent = settings.agents.find((item) => item.id === change.id);
      if (!agent) return unknown(change.id);
      const direction: ChangeDirection =
        change.enabled === agent.enabled ? 'none' : change.enabled ? 'loosen' : 'tighten';
      return {
        direction,
        label: agent.name,
        from: flagState(agent.enabled),
        to: flagState(change.enabled),
        violation: null,
      };
    }
  }
}

/**
 * The trimmed reason, or '' when there is none (or it is not text at all). Invisible format
 * characters (zero-width spaces and joiners) are dropped, so they cannot pass for a reason.
 */
export function reasonText(reason: unknown): string {
  return typeof reason === 'string' ? reason.replace(/\p{Cf}/gu, '').trim() : '';
}

/** Loosening needs both a written reason and the completed hold; tightening needs neither. */
export function confirmationViolation(evaluation: ChangeEvaluation, input: PolicyChangeInput): PolicyViolation | null {
  if (evaluation.direction !== 'loosen') return null;
  if (reasonText(input.reason).length < MIN_REASON_LENGTH) {
    return { code: 'reason_required', message: 'Gevşetmek için gerekçe yaz' };
  }
  if (input.confirmedByHold !== true) {
    return { code: 'hold_required', message: 'Gevşetmek için 1,5 sn basılı tut' };
  }
  return null;
}

/** The settings after the change, as a new object (the input is not modified). */
export function applyChange(settings: Settings, change: PolicyChange, at: number): Settings {
  switch (change.kind) {
    case 'number':
      return {
        ...settings,
        policies: settings.policies.map((policy) =>
          policy.id === change.id
            ? {
                ...policy,
                value: change.value,
                updatedAt: at,
                suggestion: policy.suggestion?.value === change.value ? null : policy.suggestion,
              }
            : policy,
        ),
      };
    case 'stage':
      return { ...settings, stage: { ...settings.stage, value: change.value, updatedAt: at } };
    case 'flag':
      return {
        ...settings,
        flags: settings.flags.map((flag) =>
          flag.id === change.id ? { ...flag, enabled: change.enabled, updatedAt: at } : flag,
        ),
      };
    case 'agent':
      return {
        ...settings,
        agents: settings.agents.map((agent) =>
          agent.id === change.id ? { ...agent, enabled: change.enabled, updatedAt: at } : agent,
        ),
      };
  }
}
